/** Stating a company's financials and asking for a determination — **ADR-0069, card C-F, F-045**.
 *
 * Two writes, both Admin's, and one rule that is the reason this file exists:
 *
 * **A yes/no answer the person did not give is not sent.** `entity_financials` has five `NOT NULL`
 * booleans, and a row made from "turnover is £40m" used to record five answers nobody gave. The
 * rules read a recorded `false` as a *known fact*, so SB 253 came out `NOT IN SCOPE` with
 * `undetermined = false` for a company that was never asked. The server now refuses a first
 * statement that omits any of the five (`booleans_required`); this file is the client's half: an
 * unanswered question is **absent from the request body**, never `false`.
 *
 * That is `buildStatement`, and it is a pure function so the property can be asserted — and
 * mutation-proved — without rendering anything. A control that defaulted to *no* would make
 * `"is_quoted" in body` true for a question nobody touched, and `financials.test.tsx` fails.
 *
 * **Nothing here decides whether a company files.** The status, the reasoning, the missing facts,
 * the caveat and the date's basis all arrive on the response; the screen renders them.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { api, keys } from "./client";
import { refusalFrom, Refused } from "./refusal";
import type { components } from "./schema";

export type Statement = components["schemas"]["FinancialsIn"];
export type Stated = components["schemas"]["FinancialsStatedOut"];
export type Determination = components["schemas"]["DeterminationOut"];
export type Determined = components["schemas"]["DetermineOut"];

/** The yes/no questions that have a **column**, in the order the server asks them
 * (`financials.BOOLEAN_FACTS`).
 *
 * **Never defaulted, never inferred.** The `satisfies` and the check below make the compiler hold
 * the list to the generated schema: a fifth yes/no field added to `FinancialsIn` and forgotten here
 * is a type error rather than a question the form silently never asks.
 *
 * A regime's own questions have no column (ADR-0205): the rules of every loaded regime name them,
 * `GET /api/v1/regimes` lists them as `extra_facts`, and they travel in `Statement.facts`. The form
 * asks `BOOLEAN_FACTS` and then those.
 */
export const BOOLEAN_FACTS = [
  "is_quoted",
  "is_public_company",
  "is_insurance_business",
  "included_in_parent_group_report",
] as const satisfies readonly (keyof Statement)[];

/** A yes/no question by its fact name: one of the columns above or a regime's own. Open, because a
 * regime loaded as data names its own and the SPA must not need a code change to ask it. */
export type BooleanFact = string;

type SchemaBooleans = {
  [K in keyof Statement]-?: NonNullable<Statement[K]> extends boolean ? K : never;
}[keyof Statement];

/** Compile-time only: resolves to `true` while the list above is exactly the schema's booleans. */
export const BOOLEAN_FACTS_COMPLETE: [SchemaBooleans] extends [(typeof BOOLEAN_FACTS)[number]]
  ? true
  : never = true;

const COLUMN_FACTS: ReadonlySet<string> = new Set(BOOLEAN_FACTS);

/** Every question a first statement must answer: the columns', then the loaded regimes' own,
 * de-duplicated and in a stable order. */
export function questionsFor(extraFacts: readonly string[]): string[] {
  return [...BOOLEAN_FACTS, ...extraFacts.filter((fact) => !COLUMN_FACTS.has(fact))].filter(
    (fact, index, all) => all.indexOf(fact) === index,
  );
}

/** The quantities, as the form offers them. `kind` decides the shape a value must have before it is
 * worth sending; the server's bounds and its currency rules remain the authority.
 *
 * `money` is stated in the one `turnover_currency`; `usd` names its own unit, so it is not.
 */
export type FigureKind = "money" | "usd" | "count" | "energy";

export interface FigureField {
  key: Exclude<keyof Statement, (typeof BOOLEAN_FACTS)[number] | "facts" | "turnover_currency">;
  kind: FigureKind;
}

export const FIGURES: readonly FigureField[] = [
  { key: "turnover", kind: "money" },
  { key: "balance_sheet_total", kind: "money" },
  { key: "employees", kind: "count" },
  { key: "revenue_usd", kind: "usd" },
  { key: "prior_year_revenue_usd", kind: "usd" },
  { key: "group_turnover_net", kind: "money" },
  { key: "group_turnover_gross", kind: "money" },
  { key: "group_balance_sheet_net", kind: "money" },
  { key: "group_balance_sheet_gross", kind: "money" },
  { key: "group_employees", kind: "count" },
  { key: "uk_energy_kwh", kind: "energy" },
];

export type FigureKey = FigureField["key"];

/** Whether a typed quantity has a shape the column can hold. **Exact decimal strings, never a
 * float** (rule 5): the value travels as the text the person typed. The server's own bounds
 * (`ge=0`, digits, places) remain authoritative; this only stops a request certain to be a 422. */
export function figureShapeOk(kind: FigureKind, text: string): boolean {
  switch (kind) {
    case "money":
    case "usd":
      return /^\d{1,18}(\.\d{1,2})?$/.test(text);
    case "energy":
      return /^\d{1,17}(\.\d{1,3})?$/.test(text);
    case "count":
      return /^\d{1,10}$/.test(text) && Number(text) <= 2_147_483_647;
  }
}

export type Answers = Partial<Record<BooleanFact, boolean>>;

/** The questions nobody has answered, in the order given. `questions` defaults to the columns' own
 * (`BOOLEAN_FACTS`); the form passes `questionsFor(...)` so a regime's questions are included. */
export function unanswered(
  answers: Answers,
  questions: readonly BooleanFact[] = BOOLEAN_FACTS,
): BooleanFact[] {
  return questions.filter((fact) => answers[fact] === undefined);
}

export interface StatementInput {
  /** `undefined` is *not asked*, which is a different thing from `false`. */
  answers: Answers;
  /** A three-letter code, or empty for none stated. */
  currency: string;
  /** What was typed, by field. Empty and whitespace-only are *not stated*. */
  figures: Partial<Record<FigureKey, string>>;
}

/** The request body — and **the one place a yes/no answer enters it.**
 *
 * Each boolean is added only where the person chose one. There is no `?? false`, no spread of a
 * defaulted record and no loop over `BOOLEAN_FACTS` that writes a value for every key: any of those
 * is F-045 wearing a tidy expression, and `financials.test.tsx` parses this function's output for
 * exactly that.
 *
 * Empty figures are omitted rather than sent as `null`: on this API an absent field is *not
 * stated* and `null` *clears* a stored quantity, and a form that cleared what it merely left blank
 * would delete figures nobody meant to delete.
 */
export function buildStatement(input: StatementInput): Statement {
  const body: Statement = {};
  const facts: Record<string, boolean> = {};
  // Iterates the **answers given**, never a list of questions: a question nobody answered is not
  // visited, so it cannot be written as `false`. A column's answer goes at the top level; a
  // regime's own goes in `facts`, named by its fact.
  for (const [fact, answer] of Object.entries(input.answers)) {
    if (answer === undefined) continue;
    if (COLUMN_FACTS.has(fact)) {
      (body as Record<string, boolean>)[fact] = answer;
    } else {
      facts[fact] = answer;
    }
  }
  if (Object.keys(facts).length > 0) body.facts = facts;
  // **The currency labels the size (`money`) figures only.** It is sent beside one and not otherwise:
  // a currency with no size figure labels nothing, and `revenue_usd` names its own unit.
  const currency = input.currency.trim().toUpperCase();
  const sizeStated = FIGURES.some(
    (field) => field.kind === "money" && Boolean(input.figures[field.key]?.trim()),
  );
  if (currency && sizeStated) body.turnover_currency = currency;
  // Keyed by `FigureKey`, whose members `FIGURES` and the schema's `Statement` agree on; the
  // per-key value type (a count is a number, a decimal is a string) follows `kind`.
  const figures: Partial<Record<FigureKey, string | number>> = {};
  for (const field of FIGURES) {
    const text = input.figures[field.key]?.trim();
    if (!text) continue;
    // Counts are JSON numbers in the schema; every decimal is the exact string the person typed.
    figures[field.key] = field.kind === "count" ? Number(text) : text;
  }
  return { ...body, ...(figures as Partial<Statement>) };
}

/** A refusal of a statement, with the fields the server says it is about.
 *
 * `fields` is the server's own list (`ReadinessRefusedError.fields`) and is what lets a screen
 * *name* the unanswered questions rather than parse them out of a sentence written to be reworded.
 * An empty list means the refusal is not about particular fields.
 */
export class StatementRefused extends Refused {
  readonly fields: readonly string[];

  constructor(base: Refused, fields: readonly string[]) {
    super(base.status, base.code, base.message, base.currentVersion);
    this.name = "StatementRefused";
    this.fields = fields;
  }
}

function fieldsOf(error: unknown): string[] {
  if (!error || typeof error !== "object" || !("detail" in error)) return [];
  const detail: unknown = error.detail;
  if (!detail || typeof detail !== "object" || !("fields" in detail)) return [];
  const fields: unknown = detail.fields;
  return Array.isArray(fields) ? fields.filter((f): f is string => typeof f === "string") : [];
}

/** Whether a thrown value is the server saying a yes/no answer was not given. */
export function isBooleansRequired(error: unknown): error is StatementRefused {
  return error instanceof StatementRefused && error.code === "booleans_required";
}

/** State a company's financials for a year — and, on the server, determine in the same transaction.
 *
 * Invalidates the obligation list on success, because the statement has just changed what the
 * Readiness screen should say. It is **not** invalidated on a refusal: nothing was written.
 */
export function useStateFinancials() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      entityId: string;
      year: number;
      body: Statement;
    }): Promise<Stated> => {
      const { data, error, response } = await api.PUT(
        "/api/v1/readiness/entities/{entity_id}/financials/{year}",
        { params: { path: { entity_id: input.entityId, year: input.year } }, body: input.body },
      );
      if (data === undefined || error !== undefined) {
        throw new StatementRefused(
          refusalFrom(response, error, "That statement could not be saved."),
          fieldsOf(error),
        );
      }
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.obligations() }),
  });
}

/** Determine every company's position for a year — the Admin's explicit act (ADR-0069 2A).
 *
 * The only way to introduce a year or to pick up a newer rules version. With no financials stated
 * it still answers with one `undetermined` position per company per regime, naming what it needs.
 */
export function useDetermine() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { year: number }): Promise<Determined> => {
      const { data, error, response } = await api.POST("/api/v1/readiness/determine", {
        body: { year: input.year },
      });
      if (data === undefined || error !== undefined) {
        throw refusalFrom(response, error, "The determination could not be run.");
      }
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.obligations() }),
  });
}
